import { Injectable } from '@angular/core';
import {environment} from '../../../environments/environment';
import {ScgHttpWrapper} from '@app/core/injectables/scg-http-wrapper';
import {DeviceService} from '@app/core/services/device.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {Device} from '@app/core/models/device';
import {tap} from 'rxjs/operators';
import { AuthService, UserRole } from '../auth/auth.service';
import {ModaldevComponent, ModalDevData} from '@app/shared/modals/modaldev/modaldev.component';
import {ModalButton, ModalButtons} from '@app/core/models/modal-ref';
import {NGXLogger} from 'ngx-logger';
import {ModalService} from '@app/core/services/modal.service';
import {ModalmissingrightComponent} from '@app/shared/modals/modalmissingright/modalmissingright.component';
import {ScgSharedFactoryService} from '@app/core/services/scg-shared-factory.service';

@Injectable({
  providedIn: 'root'
})
export class ApiNotificationService {
  /** key = deviceId */
  private notificationCountsSubjects: Map<string, BehaviorSubject<NotificationCounts>> = new Map();


  constructor(
    private scgHttp: ScgHttpWrapper,
    private auth: AuthService,
    private scgShared: ScgSharedFactoryService,
    private log: NGXLogger,
    private modalService: ModalService,
    private device: DeviceService
  ) { }

  public init() {
    this.device.onDeviceSwitch.subscribe(newDevice => {
      if (this.auth.role === UserRole.Default || this.auth.role === UserRole.Parents) {
        this.refreshNotificationCounts(newDevice.device);
      }
    });

    this.auth.onLogout.subscribe(() => {
      for (const [, subject] of this.notificationCountsSubjects) {
        subject.complete();
      }
      this.notificationCountsSubjects.clear();
    });
  }

  public getNotificationCounts(deviceId: string): Observable<NotificationCounts> {
    return this.initCountsListener(deviceId).asObservable();
  }

  public getNotifications(deviceId) {
    return this.scgHttp.withDevice(deviceId).get(environment.apiUrl + 'notification');
  }

  public remove(deviceId: string, msgId: string, delDays: number) {
    const postdata = {id : msgId, deleteDays: delDays};
    return this.scgHttp.withDevice(deviceId).post(environment.apiUrl + 'notification/remove', postdata).pipe(
      tap(() => this.refreshCounter(deviceId))
    );
  }

  public refreshCounter(deviceId: string) {
    this.refreshNotificationCounts(deviceId);
  }

  public hasNotifications(deviceId) {
    return this.scgHttp.withDevice(deviceId).
    get<{ notificationCount: number, unlockCount: number, deviceStatus: string, extensionRequest: boolean, hasIssue: boolean; isUpdateAvailable: boolean; alertCount: number }>(environment.apiUrl + 'notification/count');
  }

    public getExtensionRequest(deviceId) {
    return this.scgHttp.withDevice(deviceId).get<any>(environment.apiUrl + 'notification/extensionRequest');
  }

  public deleteExtensionRequest(deviceId, user) {
    const postdata = {username : user};
    return this.scgHttp.withDevice(deviceId).post(environment.apiUrl + 'notification/deleteextensionrequest', postdata);
  }

  public restore(deviceId: string) {
    return this.scgHttp.withDevice(deviceId).get(environment.apiUrl + 'notification/restore').pipe(
      tap(() => this.refreshNotificationCounts(deviceId))
    );
  }

  private refreshNotificationCounts(deviceId: string|Device) {
    let device;
    if (deviceId instanceof Device) {
      device = deviceId;
    } else {
      device = this.device.devices.find(d => d.id === deviceId);
    }

    if (!this.notificationCountsSubjects.get(device.id)) {
      // No refresh, this is first pull
      this.initCountsListener(device.id);
      return;
    }

    this.hasNotifications(device.id).subscribe(response => {
      const notificationCountsSubject = this.initCountsListener(device.id);
      // deviceStatus MUST stay in this diff check — see .claude/notes_notification_counts_diff_check.md
      if (notificationCountsSubject.value.notificationCount !== response.notificationCount
        || notificationCountsSubject.value.unlockCount !== response.unlockCount
        || notificationCountsSubject.value.extensionRequest !== response.extensionRequest
        || notificationCountsSubject.value.hasIssue !== response.hasIssue
        || notificationCountsSubject.value.isUpdateAvailable !== response.isUpdateAvailable
        || notificationCountsSubject.value.alertCount !== response.alertCount
        || notificationCountsSubject.value.deviceStatus !== response.deviceStatus) {
        notificationCountsSubject.next({
          notificationCount: response.notificationCount,
          unlockCount: response.unlockCount,
          extensionRequest: response.extensionRequest,
          hasIssue: response.hasIssue,
          isUpdateAvailable: response.isUpdateAvailable,
          alertCount: response.alertCount,
          deviceStatus: response.deviceStatus
        });
      }
    });
  }

  private initCountsListener(deviceId: string): BehaviorSubject<NotificationCounts> {
    let notificationCountsSubject = this.notificationCountsSubjects.get(deviceId);

    if (!notificationCountsSubject) {
      notificationCountsSubject = new BehaviorSubject<NotificationCounts>({
        notificationCount: 0,
        unlockCount: 0,
        extensionRequest: false,
        isUpdateAvailable: false,
        hasIssue: false,
        alertCount: 0,
        deviceStatus: ''
      });
      this.notificationCountsSubjects.set(deviceId, notificationCountsSubject);

      this.hasNotifications(deviceId).subscribe(response => {
        notificationCountsSubject.next({
          notificationCount: response.notificationCount,
          unlockCount: response.unlockCount,
          extensionRequest: response.extensionRequest,
          hasIssue: response.hasIssue,
          isUpdateAvailable: response.isUpdateAvailable,
          alertCount: response.alertCount,
          deviceStatus: response.deviceStatus
        });
      });
    }

    return notificationCountsSubject;
  }


}




export interface NotificationCounts {
  notificationCount: number;
  unlockCount: number;
  extensionRequest: boolean;
  hasIssue: boolean;
  isUpdateAvailable: boolean;
  alertCount: number;
  deviceStatus: string;

}
